import { Check } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { ASSETS } from "@/lib/assets";
import { SEO } from "@/components/SEO";

export const Forsyninger = () => {
  return (
    <>
      <SEO
        title="Kloakpartner for forsyningsselskaber"
        description="Farum Kloak arbejder med forsyningsselskaber om kritisk infrastruktur: drift, anlæg, akut respons og dokumentation, der kan stå på mål overfor både drift og ledelse."
      />
      <PageHero
        eyebrow="Segment · Forsyninger"
        title={
          <>
            Driftssikre løsninger til <em className="text-[color:var(--fk-gold)]">kritisk infrastruktur.</em>
          </>
        }
        subtitle="Farum Kloak hjælper forsyningsselskaber med kloakopgaver, hvor drift, dokumentation og risikostyring skal hænge sammen. Vi bidrager med faglig sparring, hurtig respons og løsninger, der kan holde til både hverdag, kontrol og ansvar."
        image={ASSETS.images.utility}
      />

      <section className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <p className="font-display text-2xl md:text-3xl leading-snug italic text-white/95">
              Vi forstår konstruktioner, jord, vandtryk og ansvar. Og vi ved, at
              en god løsning både skal fungere i driften og kunne forklares
              videre i organisationen.
            </p>
          </div>
          <div className="md:col-span-7">
            <div className="eyebrow mb-6">Det får du</div>
            <ul className="space-y-5">
              {[
                "Stabilt samarbejde med fokus på drift, fremdrift og risikominimering",
                "Gennemsigtige priser, klare aftaler og åben kommunikation",
                "Dokumentation og rapportering, der kan bruges internt og eksternt",
                "Faglig sparring i både planlægning, udførelse og opfølgning",
                "Forståelse for lovkrav, miljøansvar og forsyningens omdømme",
              ].map((b, i) => (
                <li key={i} className="flex gap-4 text-lg text-white/80 font-light border-b border-white/10 pb-5">
                  <Check className="w-5 h-5 flex-shrink-0 mt-1.5 text-[color:var(--fk-gold)]" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="border-t border-white/10 bg-[color:var(--fk-bg-2)]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-12">
            <div className="md:col-span-5">
              <div className="eyebrow mb-4">Områder</div>
              <h2 className="font-display text-4xl md:text-5xl leading-tight">
                Hvor vi <em className="text-[color:var(--fk-gold)]">skaber værdi.</em>
              </h2>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10">
            {[
              { title: "Drift", body: "Vedligehold, fejlfinding og løbende service på kloak og afvanding, hvor stabil drift er afgørende." },
              { title: "Anlæg", body: "Renovering, udvidelse og etablering af rørledninger, brønde og afvandingsløsninger i forsyningsprojekter." },
              { title: "Akut", body: "Hurtig respons ved skader, tilstopninger og driftskritiske hændelser, hvor der skal handles med det samme." },
            ].map((c, i) => (
              <div key={i} className="bg-[color:var(--fk-bg-2)] p-10 md:p-14">
                <div className="eyebrow mb-6 text-[color:var(--fk-gold)]">0{i + 1}</div>
                <h3 className="font-display text-2xl md:text-3xl mb-4">{c.title}</h3>
                <p className="text-white/60 font-light">{c.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28 grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-7">
            <div className="eyebrow mb-6">Næste skridt</div>
            <h2 className="font-display text-4xl md:text-5xl leading-tight">
              Skal vi tage <em className="text-[color:var(--fk-gold)]">dialogen?</em>
            </h2>
            <p className="mt-6 text-white/60 font-light max-w-xl">
              Ring eller skriv til os, hvis I har brug for en kloakpartner til
              drift, anlæg eller akutte opgaver. Vi tager gerne dialogen tidligt,
              så løsningen kan vurderes ordentligt fra start.
            </p>
          </div>
          <div className="md:col-span-5 flex flex-col sm:flex-row gap-4">
            <a href={ASSETS.brand.phoneTel} className="px-7 py-4 text-[11px] tracking-[0.24em] uppercase bg-[color:var(--fk-gold)] text-black hover:bg-[color:var(--fk-gold-hi)] transition-colors text-center">
              Ring {ASSETS.brand.phone}
            </a>
            <a href={`mailto:${ASSETS.brand.email}`} className="px-7 py-4 text-[11px] tracking-[0.24em] uppercase border border-white/20 hover:border-[color:var(--fk-gold)] hover:text-[color:var(--fk-gold)] transition-colors text-center">
              Skriv til os
            </a>
          </div>
        </div>
      </section>
    </>
  );
};
